// Bold gradient card styling — shared across the app's "summary" cards
// (metric, payment, maintenance, chat, agreement, service-person) so they
// all get the same look: a status gradient fill, white text/icons for
// contrast, a soft status-colored shadow, and a hover "lift".
//
// Smart states: vivid tones (green/amber/red/indigo) carry meaning and use
// WHITE text. The muted "slate" tone is for locked / inactive / archived
// items — it greys the card out and uses DARK text so contrast stays
// AA-compliant (white on light-grey would fail).

export const GRADS = {
  green:  ["#34d399", "#059669"], // good / done / active
  amber:  ["#fbbf24", "#d97706"], // needs attention / expiring / pending
  red:    ["#fb7185", "#e11d48"], // overdue / bad
  indigo: ["#60a5fa", "#4f46e5"], // neutral brand (chat, marketplace)
  violet: ["#a78bfa", "#7c3aed"], // active / in-progress
  slate:  ["#cbd5e1", "#94a3b8"], // muted / locked / inactive (dark text)
};

// The slate tone is light enough that text must stay dark, not white.
export const isLightTone = (tone) => tone === "slate";

export const gradBg = (tone) => {
  const [a, b] = GRADS[tone] || GRADS.slate;
  return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`;
};

export const gradHue = (tone) => (GRADS[tone] || GRADS.slate)[1];

// Spread over crd(h) to convert a normal card into a gradient card.
// Keeps crd's padding / radius / marginBottom; overrides bg/border/shadow.
export const gradCardStyle = (tone) => ({
  background: gradBg(tone),
  border: "none",
  boxShadow: `0 6px 18px ${gradHue(tone)}55`,
  transition: "transform 0.2s, box-shadow 0.2s",
});

// Hover "lift" handlers. Usage:
//   onMouseOver={(e) => gradLift(e, tone, true)}
//   onMouseOut={(e) => gradLift(e, tone, false)}
export const gradLift = (e, tone, on) => {
  e.currentTarget.style.transform = on ? "translateY(-2px)" : "none";
  e.currentTarget.style.boxShadow = on
    ? `0 10px 26px ${gradHue(tone)}66`
    : `0 6px 18px ${gradHue(tone)}55`;
};

// Glassy translucent-white status pill that reads on any vivid gradient.
export const glassPill = {
  display: "inline-flex", alignItems: "center",
  background: "rgba(255,255,255,0.25)", color: "#fff",
  padding: "3px 10px", borderRadius: 999,
  fontSize: 11, fontWeight: 700, letterSpacing: "0.1px", lineHeight: 1.4,
};

// Glassy white button tile with a colored label — keeps semantic colors
// (e.g. red = delete) readable on a gradient. Mirrors the payment-card
// action buttons.
export const glassTile = (color) => ({
  flex: 1, padding: 8, borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.92)",
  color, cursor: "pointer", fontSize: 12, fontWeight: 600,
});

// Glass panel that floats on a vivid gradient card and hosts normal
// theme-token content (h.tx text, bdg pills, etc.). Must follow the
// theme: a hardcoded white panel made dark-mode text invisible
// (h.tx is #f5f5f7 in dark mode → white-on-white).
export const glassPanel = (h) => ({
  background: h && h.dk ? "rgba(17,17,22,0.92)" : "rgba(255,255,255,0.95)",
  border: h && h.dk ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(255,255,255,0.7)",
});

// Fixed dark ink for content sitting on the light "slate" gradient.
// Theme tokens must NOT be used there — dark-mode h.tx is near-white,
// which would render white-on-light-grey (~1.5:1).
export const SLATE_INK = { tx: "#1e293b", sub: "#475569" };
